import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell, PageHero } from "@/components/marketing/PageShell";
import { BrowserFrame } from "@/components/marketing/BrowserFrame";
import { LucideIcon } from "@/components/marketing/LucideIcon";
import { FEATURES } from "@/content/pages";

// Generic product-feature page (Discovery, Governance).
export default function FeaturePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.feature;
  const f = FEATURES[slug];
  if (!f) {
    return (
      <PageShell testid="feature-not-found" seo={{ title: "Not found — Sema", description: "Page not found", path: "/" }}>
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
          <p className="font-mono text-sm text-muted-foreground">No feature page for "{slug}".</p>
          <Link to="/product" className="font-mono text-xs uppercase tracking-[0.18em] text-accent">See the product →</Link>
        </div>
      </PageShell>
    );
  }
  return (
    <PageShell testid={`feature-${f.slug}-root`} seo={{
      title: f.metaTitle, description: f.metaDescription, keywords: f.keywords, path: `/${f.slug}`,
    }}>
      <PageHero testid={`feature-${f.slug}-h1`} eyebrow={f.eyebrow} accent={f.accent}
        title={f.h1} subtitle={f.subtitle} />

      <section className="py-8">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-5 px-6 md:grid-cols-3">
          {f.blocks.map((b) => (
            <div key={b.title} className="rounded-2xl border border-border/60 bg-card/50 p-7 backdrop-blur">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: `${f.accent}1f`, color: f.accent }}>
                <LucideIcon name={b.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-semibold tracking-tight">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-6 lg:grid-cols-2 lg:gap-10">
          {f.shots.map((s, i) => (
            <BrowserFrame key={i} src={s.src} alt={f.h1} caption={s.caption} eager={i === 0} />
          ))}
        </div>
      </section>

      <section className="py-20 text-center">
        <h2 className="text-3xl font-semibold tracking-tight">See it on your data.</h2>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/signup"><Button className="rounded-full px-6 font-mono text-xs uppercase tracking-[0.2em] text-white hover:opacity-90" style={{ backgroundColor: f.accent }}>Start free sandbox <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          <Link to="/product"><Button variant="outline" className="rounded-full border-border/70 px-6 font-mono text-xs uppercase tracking-[0.2em]">How it works</Button></Link>
        </div>
      </section>
    </PageShell>
  );
}
